import { sortBy } from 'lodash';
import { useMemo } from 'react';
import type { TaskQuery } from '~/gql/graphql';

/**
 * Adds location and space separators to assets array for display purposes.
 * Groups assets by location and space, inserting separator strings between groups.
 */
function addLocationAndSpaceSeparators(assets: TaskQuery['task']['assets']) {
  return assets.reduce(
    (
      prev: {
        location: string;
        space: string;
        all: Array<TaskQuery['task']['assets'][number] | string>;
      },
      curr
    ) => {
      const locationName = curr.deployment?.location?.name || '';
      const spaceName = curr.deployment?.space?.name || '';

      if (locationName && locationName !== prev.location) {
        prev.location = locationName;
        prev.all.push(`location:${locationName}`);
        prev.space = ''; // Reset space when location changes
      }

      if (spaceName && spaceName !== prev.space) {
        prev.space = spaceName;
        prev.all.push(`space:${spaceName}`);
      }
      prev.all.push(curr);
      return prev;
    },
    {
      location: '',
      space: '',
      all: [] as Array<TaskQuery['task']['assets'][number] | string>,
    }
  ).all;
}

/**
 * Hook that processes task assets for display.
 * Handles filtering for specific asset selection (parsing task name),
 * sorting by location and space, and adding location/space separators.
 *
 * @param task - The task containing assets to process
 * @returns Processed assets array with location/space separators
 */
export function useTaskAssets(task: TaskQuery['task']) {
  return useMemo(() => {
    let assetsToDisplay = task.assets;

    // For asset-driven tasks with specific selection, filter to show only the assigned asset
    // Asset-driven tasks have names like "Task Name - Asset Name"
    if (
      task.type === 'Asset' &&
      task.config?.selection === 'specific' &&
      task.name.includes(' - ')
    ) {
      // Extract asset name from task name pattern: "Task Name - Asset Name"
      const assetNameFromTask = task.name.split(' - ').slice(-1)[0]?.trim();

      if (assetNameFromTask) {
        // Filter assets to only show the one matching the asset name from the task name
        // This ensures we show only the specific assigned asset, not all matching assets
        const matchedAssets = task.assets.filter(
          (asset) => asset.name === assetNameFromTask
        );
        // Use matched assets if found, otherwise fall back to all assets (shouldn't happen)
        if (matchedAssets.length > 0) {
          assetsToDisplay = matchedAssets;
        }
      }
    }

    const sortedAssets = sortBy(assetsToDisplay, [
      'deployment.location.name',
      'deployment.space.name',
    ]);
    return addLocationAndSpaceSeparators(sortedAssets);
  }, [task.assets, task.type, task.config?.selection, task.name]);
}
